/**
 * KAN-826 этап 3: фильтры строк доски CRM по городу и организации (на клиенте).
 * Чистые функции над элементами вида { row: CrmRow }, как filterRowsByAttribute.
 */
import type { CrmRow } from '@/types/crm';

type WithRow<K extends keyof CrmRow> = { row: Pick<CrmRow, K> };

const norm = (value: string): string => value.trim().toLocaleLowerCase('ru').replace(/ё/g, 'е');

/** Различные непустые города строк по алфавиту; выбранный город остаётся в списке. */
export function cityOptions<T extends WithRow<'city'>>(items: readonly T[], selected?: string | null): string[] {
  const byKey = new Map<string, string>();
  const add = (raw: string | null | undefined) => {
    const city = raw?.trim();
    if (!city) return;
    const key = city.toLocaleLowerCase('ru');
    if (!byKey.has(key)) byKey.set(key, city);
  };
  items.forEach((item) => add(item.row.city));
  add(selected);
  return [...byKey.values()].sort((a, b) => a.localeCompare(b, 'ru', { sensitivity: 'base' }));
}

export function filterRowsByCity<T extends WithRow<'city'>>(items: T[], city: string | null | undefined): T[] {
  const wanted = city?.trim().toLocaleLowerCase('ru');
  if (!wanted) return items;
  return items.filter((item) => item.row.city?.trim().toLocaleLowerCase('ru') === wanted);
}

export function filterRowsByCompany<T extends WithRow<'clinic' | 'client_company'>>(
  items: T[],
  text: string | null | undefined
): T[] {
  const wanted = text ? norm(text) : '';
  if (!wanted) return items;
  return items.filter((item) => {
    const name = item.row.client_company?.name || item.row.clinic;
    return !!name && norm(name).includes(wanted);
  });
}

export function filterRowsByCompanyId<T extends WithRow<'client_company'>>(
  items: T[],
  companyId: string | null | undefined
): T[] {
  if (!companyId) return items;
  return items.filter((item) => item.row.client_company?.id === companyId);
}
